// Language and Translation System for Axiom ARP
// Supported (Sep 2026): English, Russian, Ukrainian, Spanish, Italian,
// Korean, Portuguese, Mandarin (Simplified). French + Traditional Chinese
// removed per Tudor's 30 Sep 2026 spec — any user stored on 'fr' or
// 'zh-TW' silently falls back to English.

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: 'Mandarin (Simplified)', nativeName: '中文 (简体)', flag: '🇨🇳' },
];

// UI Translations - These are system/interface strings that can be translated programmatically
// Clinical content translations will be provided by the user and stored separately

export const UI_TRANSLATIONS = {
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      journal: 'Journal',
      mentalHealth: 'Mental Health',
      meetings: 'Meetings',
      chat: 'Chat with Tudor',
      library: 'Resources',
      inbox: 'Inbox',
      group: 'RR',
      menu: 'Menu',
      journey: 'Journey'
    },
    
    // Dashboard
    dashboard: {
      welcome: 'Welcome back',
      twoWeekWindow: '2 Week Window',
      week: 'Week',
      dailyTasks: 'Daily Tasks',
      weeklyTasks: 'Weekly Tasks',
      progress: 'Progress',
      sobrietyClock: 'Sobriety Clock',
      notifications: 'Notifications'
    },
    
    // Tasks
    tasks: {
      journal: 'Daily Journal',
      mentalHealthCheck: 'Mental Health Check-in',
      personalHistory: 'Personal History',
      weeklyReadings: 'Weekly Readings',
      selfWork: 'Self-Work',
      meetingLog: 'Meeting Log',
      completed: 'Completed',
      pending: 'Pending',
      required: 'Required',
      optional: 'Optional'
    },
    
    // Notifications
    notifications: {
      urgent: 'URGENT',
      drugScreenRequest: 'Drug Screen Required',
      attendWithin24Hours: 'Please attend your nearest pathology lab within 24 hours',
      uploadResults: 'Upload your results',
      newMessage: 'New message',
      taskReminder: 'Task reminder',
      weekComplete: 'Week complete!',
      readyToProgress: 'Ready to progress to next week'
    },
    
    // Drug Screening
    drugScreen: {
      title: 'Drug Screen Request',
      instruction: 'You have been requested to complete a urine drug screen',
      deadline: 'Deadline',
      labs: 'Approved Labs',
      uploadResults: 'Upload Results',
      scanOrPhoto: 'Scan or photograph your results',
      yourId: 'Your ID Number',
      status: 'Status',
      pending: 'Pending',
      submitted: 'Submitted',
      verified: 'Verified'
    },
    
    // Buttons & Actions
    actions: {
      save: 'Save',
      submit: 'Submit',
      cancel: 'Cancel',
      continue: 'Continue',
      back: 'Back',
      upload: 'Upload',
      download: 'Download',
      edit: 'Edit',
      delete: 'Delete',
      complete: 'Mark Complete',
      viewAll: 'View All',
      signIn: 'Sign In',
      signOut: 'Sign Out',
      register: 'Register'
    },
    
    // Messages
    messages: {
      loading: 'Loading...',
      saving: 'Saving...',
      saved: 'Saved successfully',
      error: 'An error occurred',
      required: 'This field is required',
      taskCompleted: 'Task completed!',
      pleaseComplete: 'Please complete this task',
      completeToProgress: 'Complete all required tasks to progress'
    },
    
    // Time
    time: {
      days: 'days',
      hours: 'hours',
      minutes: 'minutes',
      seconds: 'seconds',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week'
    }
  },
  
  ru: {
    nav: {
      dashboard: 'Панель',
      journal: 'Дневник',
      mentalHealth: 'Психическое здоровье',
      meetings: 'Встречи',
      chat: 'Чат с Tudor',
      library: 'Ресурсы',
      inbox: 'Входящие',
      group: 'RR',
      menu: 'Меню',
      journey: 'Путь'
    },
    dashboard: {
      welcome: 'С возвращением',
      twoWeekWindow: '2-недельное окно',
      week: 'Неделя',
      dailyTasks: 'Ежедневные задачи',
      weeklyTasks: 'Еженедельные задачи',
      progress: 'Прогресс',
      sobrietyClock: 'Часы трезвости',
      notifications: 'Уведомления'
    },
    tasks: {
      journal: 'Ежедневный дневник',
      mentalHealthCheck: 'Проверка психического здоровья',
      personalHistory: 'Личная история',
      weeklyReadings: 'Еженедельные чтения',
      selfWork: 'Самостоятельная работа',
      meetingLog: 'Журнал встреч',
      completed: 'Завершено',
      pending: 'В ожидании',
      required: 'Обязательно',
      optional: 'Необязательно'
    },
    notifications: {
      urgent: 'СРОЧНО',
      drugScreenRequest: 'Требуется тест на наркотики',
      attendWithin24Hours: 'Пожалуйста, посетите ближайшую лабораторию в течение 24 часов',
      uploadResults: 'Загрузите результаты',
      newMessage: 'Новое сообщение',
      taskReminder: 'Напоминание о задаче',
      weekComplete: 'Неделя завершена!',
      readyToProgress: 'Готовы перейти к следующей неделе'
    },
    drugScreen: {
      title: 'Запрос на тест',
      instruction: 'Вам необходимо пройти тест на наркотики',
      deadline: 'Срок',
      labs: 'Одобренные лаборатории',
      uploadResults: 'Загрузить результаты',
      scanOrPhoto: 'Отсканируйте или сфотографируйте результаты',
      yourId: 'Ваш ID номер',
      status: 'Статус',
      pending: 'В ожидании',
      submitted: 'Отправлено',
      verified: 'Проверено'
    },
    actions: {
      save: 'Сохранить',
      submit: 'Отправить',
      cancel: 'Отмена',
      continue: 'Продолжить',
      back: 'Назад',
      upload: 'Загрузить',
      download: 'Скачать',
      edit: 'Редактировать',
      delete: 'Удалить',
      complete: 'Отметить выполненным',
      viewAll: 'Показать все',
      signIn: 'Войти',
      signOut: 'Выйти',
      register: 'Регистрация'
    },
    messages: {
      loading: 'Загрузка...',
      saving: 'Сохранение...',
      saved: 'Успешно сохранено',
      error: 'Произошла ошибка',
      required: 'Это поле обязательно',
      taskCompleted: 'Задача выполнена!',
      pleaseComplete: 'Пожалуйста, выполните эту задачу',
      completeToProgress: 'Выполните все задачи для продвижения'
    },
    time: {
      days: 'дней',
      hours: 'часов',
      minutes: 'минут',
      seconds: 'секунд',
      today: 'Сегодня',
      yesterday: 'Вчера',
      thisWeek: 'На этой неделе'
    }
  },
  
  ko: {
    nav: {
      dashboard: '대시보드',
      journal: '일지',
      mentalHealth: '정신 건강',
      meetings: '미팅',
      chat: 'Tudor와 채팅',
      library: '리소스',
      inbox: '받은 편지함',
      group: 'RR',
      menu: '메뉴',
      journey: '여정'
    },
    dashboard: {
      welcome: '환영합니다',
      twoWeekWindow: '2주 기간',
      week: '주',
      dailyTasks: '일일 과제',
      weeklyTasks: '주간 과제',
      progress: '진행',
      sobrietyClock: '금주 시계',
      notifications: '알림'
    },
    tasks: {
      journal: '일일 일지',
      mentalHealthCheck: '정신 건강 체크',
      personalHistory: '개인 이력',
      weeklyReadings: '주간 읽기',
      selfWork: '자기 학습',
      meetingLog: '미팅 기록',
      completed: '완료',
      pending: '대기 중',
      required: '필수',
      optional: '선택'
    },
    notifications: {
      urgent: '긴급',
      drugScreenRequest: '약물 검사 필요',
      attendWithin24Hours: '24시간 이내에 가까운 검사실을 방문하세요',
      uploadResults: '결과 업로드',
      newMessage: '새 메시지',
      taskReminder: '과제 알림',
      weekComplete: '주 완료!',
      readyToProgress: '다음 주로 진행 준비 완료'
    },
    drugScreen: {
      title: '약물 검사 요청',
      instruction: '소변 약물 검사를 완료해야 합니다',
      deadline: '마감',
      labs: '승인된 검사실',
      uploadResults: '결과 업로드',
      scanOrPhoto: '결과를 스캔하거나 사진 찍기',
      yourId: '귀하의 ID 번호',
      status: '상태',
      pending: '대기 중',
      submitted: '제출됨',
      verified: '확인됨'
    },
    actions: {
      save: '저장',
      submit: '제출',
      cancel: '취소',
      continue: '계속',
      back: '뒤로',
      upload: '업로드',
      download: '다운로드',
      edit: '편집',
      delete: '삭제',
      complete: '완료 표시',
      viewAll: '모두 보기',
      signIn: '로그인',
      signOut: '로그아웃',
      register: '등록'
    },
    messages: {
      loading: '로딩 중...',
      saving: '저장 중...',
      saved: '저장되었습니다',
      error: '오류가 발생했습니다',
      required: '필수 항목입니다',
      taskCompleted: '과제 완료!',
      pleaseComplete: '이 과제를 완료하세요',
      completeToProgress: '진행하려면 모든 과제를 완료하세요'
    },
    time: {
      days: '일',
      hours: '시간',
      minutes: '분',
      seconds: '초',
      today: '오늘',
      yesterday: '어제',
      thisWeek: '이번 주'
    }
  },
  
  uk: {
    nav: {
      dashboard: 'Панель',
      journal: 'Щоденник',
      mentalHealth: 'Психічне здоров\'я',
      meetings: 'Зустрічі',
      chat: 'Чат з Tudor',
      library: 'Ресурси',
      inbox: 'Вхідні',
      group: 'RR',
      menu: 'Меню',
      journey: 'Шлях'
    },
    dashboard: {
      welcome: 'З поверненням',
      twoWeekWindow: '2-тижневе вікно',
      week: 'Тиждень',
      dailyTasks: 'Щоденні завдання',
      weeklyTasks: 'Щотижневі завдання',
      progress: 'Прогрес',
      sobrietyClock: 'Годинник тверезості',
      notifications: 'Сповіщення'
    },
    tasks: {
      journal: 'Щоденний журнал',
      mentalHealthCheck: 'Перевірка психічного здоров\'я',
      personalHistory: 'Особиста історія',
      weeklyReadings: 'Щотижневі читання',
      selfWork: 'Самостійна робота',
      meetingLog: 'Журнал зустрічей',
      completed: 'Завершено',
      pending: 'Очікується',
      required: 'Обов\'язково',
      optional: 'Необов\'язково'
    },
    notifications: {
      urgent: 'ТЕРМІНОВО',
      drugScreenRequest: 'Потрібен тест на наркотики',
      attendWithin24Hours: 'Будь ласка, відвідайте найближчу лабораторію протягом 24 годин',
      uploadResults: 'Завантажте результати',
      newMessage: 'Нове повідомлення',
      taskReminder: 'Нагадування про завдання',
      weekComplete: 'Тиждень завершено!',
      readyToProgress: 'Готові перейти до наступного тижня'
    },
    drugScreen: {
      title: 'Запит на тест',
      instruction: 'Вам потрібно пройти тест на наркотики',
      deadline: 'Термін',
      labs: 'Схвалені лабораторії',
      uploadResults: 'Завантажити результати',
      scanOrPhoto: 'Відскануйте або сфотографуйте результати',
      yourId: 'Ваш ID номер',
      status: 'Статус',
      pending: 'Очікується',
      submitted: 'Надіслано',
      verified: 'Перевірено'
    },
    actions: {
      save: 'Зберегти',
      submit: 'Надіслати',
      cancel: 'Скасувати',
      continue: 'Продовжити',
      back: 'Назад',
      upload: 'Завантажити',
      download: 'Скачати',
      edit: 'Редагувати',
      delete: 'Видалити',
      complete: 'Позначити виконаним',
      viewAll: 'Показати все',
      signIn: 'Увійти',
      signOut: 'Вийти',
      register: 'Реєстрація'
    },
    messages: {
      loading: 'Завантаження...',
      saving: 'Збереження...',
      saved: 'Успішно збережено',
      error: 'Сталася помилка',
      required: 'Це поле обов\'язкове',
      taskCompleted: 'Завдання виконано!',
      pleaseComplete: 'Будь ласка, виконайте це завдання',
      completeToProgress: 'Виконайте всі завдання для просування'
    },
    time: {
      days: 'днів',
      hours: 'годин',
      minutes: 'хвилин',
      seconds: 'секунд',
      today: 'Сьогодні',
      yesterday: 'Вчора',
      thisWeek: 'Цього тижня'
    }
  },
  
  fr: {
    nav: {
      dashboard: 'Tableau de bord',
      journal: 'Journal',
      mentalHealth: 'Santé mentale',
      meetings: 'Réunions',
      chat: 'Discuter avec Tudor',
      library: 'Ressources',
      inbox: 'Boîte de réception',
      group: 'RR',
      menu: 'Menu',
      journey: 'Parcours'
    },
    dashboard: {
      welcome: 'Bon retour',
      twoWeekWindow: 'Fenêtre de 2 semaines',
      week: 'Semaine',
      dailyTasks: 'Tâches quotidiennes',
      weeklyTasks: 'Tâches hebdomadaires',
      progress: 'Progression',
      sobrietyClock: 'Horloge de sobriété',
      notifications: 'Notifications'
    },
    tasks: {
      journal: 'Journal quotidien',
      mentalHealthCheck: 'Bilan de santé mentale',
      personalHistory: 'Histoire personnelle',
      weeklyReadings: 'Lectures hebdomadaires',
      selfWork: 'Travail personnel',
      meetingLog: 'Journal des réunions',
      completed: 'Terminé',
      pending: 'En attente',
      required: 'Obligatoire',
      optional: 'Facultatif'
    },
    notifications: {
      urgent: 'URGENT',
      drugScreenRequest: 'Test de dépistage requis',
      attendWithin24Hours: 'Veuillez vous rendre au laboratoire le plus proche dans les 24 heures',
      uploadResults: 'Télécharger les résultats',
      newMessage: 'Nouveau message',
      taskReminder: 'Rappel de tâche',
      weekComplete: 'Semaine terminée!',
      readyToProgress: 'Prêt à passer à la semaine suivante'
    },
    drugScreen: {
      title: 'Demande de test',
      instruction: 'Vous devez effectuer un test urinaire de dépistage',
      deadline: 'Date limite',
      labs: 'Laboratoires agréés',
      uploadResults: 'Télécharger les résultats',
      scanOrPhoto: 'Scannez ou photographiez vos résultats',
      yourId: 'Votre numéro d\'identification',
      status: 'Statut',
      pending: 'En attente',
      submitted: 'Soumis',
      verified: 'Vérifié'
    },
    actions: {
      save: 'Enregistrer',
      submit: 'Soumettre',
      cancel: 'Annuler',
      continue: 'Continuer',
      back: 'Retour',
      upload: 'Télécharger',
      download: 'Télécharger',
      edit: 'Modifier',
      delete: 'Supprimer',
      complete: 'Marquer comme terminé',
      viewAll: 'Voir tout',
      signIn: 'Se connecter',
      signOut: 'Se déconnecter',
      register: 'S\'inscrire'
    },
    messages: {
      loading: 'Chargement...',
      saving: 'Enregistrement...',
      saved: 'Enregistré avec succès',
      error: 'Une erreur est survenue',
      required: 'Ce champ est obligatoire',
      taskCompleted: 'Tâche terminée!',
      pleaseComplete: 'Veuillez compléter cette tâche',
      completeToProgress: 'Complétez toutes les tâches pour progresser'
    },
    time: {
      days: 'jours',
      hours: 'heures',
      minutes: 'minutes',
      seconds: 'secondes',
      today: 'Aujourd\'hui',
      yesterday: 'Hier',
      thisWeek: 'Cette semaine'
    }
  },
  
  it: {
    nav: {
      dashboard: 'Pannello',
      journal: 'Diario',
      mentalHealth: 'Salute mentale',
      meetings: 'Riunioni',
      chat: 'Chatta con Tudor',
      library: 'Risorse',
      inbox: 'Posta in arrivo',
      group: 'RR',
      menu: 'Menu',
      journey: 'Percorso'
    },
    dashboard: {
      welcome: 'Bentornato',
      twoWeekWindow: 'Finestra di 2 settimane',
      week: 'Settimana',
      dailyTasks: 'Compiti giornalieri',
      weeklyTasks: 'Compiti settimanali',
      progress: 'Progresso',
      sobrietyClock: 'Orologio della sobrietà',
      notifications: 'Notifiche'
    },
    tasks: {
      journal: 'Diario giornaliero',
      mentalHealthCheck: 'Controllo salute mentale',
      personalHistory: 'Storia personale',
      weeklyReadings: 'Letture settimanali',
      selfWork: 'Lavoro personale',
      meetingLog: 'Registro riunioni',
      completed: 'Completato',
      pending: 'In attesa',
      required: 'Obbligatorio',
      optional: 'Facoltativo'
    },
    notifications: {
      urgent: 'URGENTE',
      drugScreenRequest: 'Test antidroga richiesto',
      attendWithin24Hours: 'Si prega di recarsi al laboratorio più vicino entro 24 ore',
      uploadResults: 'Carica i risultati',
      newMessage: 'Nuovo messaggio',
      taskReminder: 'Promemoria attività',
      weekComplete: 'Settimana completata!',
      readyToProgress: 'Pronto per passare alla settimana successiva'
    },
    drugScreen: {
      title: 'Richiesta test',
      instruction: 'È necessario completare un test antidroga delle urine',
      deadline: 'Scadenza',
      labs: 'Laboratori approvati',
      uploadResults: 'Carica risultati',
      scanOrPhoto: 'Scansiona o fotografa i tuoi risultati',
      yourId: 'Il tuo numero ID',
      status: 'Stato',
      pending: 'In attesa',
      submitted: 'Inviato',
      verified: 'Verificato'
    },
    actions: {
      save: 'Salva',
      submit: 'Invia',
      cancel: 'Annulla',
      continue: 'Continua',
      back: 'Indietro',
      upload: 'Carica',
      download: 'Scarica',
      edit: 'Modifica',
      delete: 'Elimina',
      complete: 'Segna come completato',
      viewAll: 'Vedi tutto',
      signIn: 'Accedi',
      signOut: 'Esci',
      register: 'Registrati'
    },
    messages: {
      loading: 'Caricamento...',
      saving: 'Salvataggio...',
      saved: 'Salvato con successo',
      error: 'Si è verificato un errore',
      required: 'Questo campo è obbligatorio',
      taskCompleted: 'Attività completata!',
      pleaseComplete: 'Si prega di completare questa attività',
      completeToProgress: 'Completa tutte le attività per progredire'
    },
    time: {
      days: 'giorni',
      hours: 'ore',
      minutes: 'minuti',
      seconds: 'secondi',
      today: 'Oggi',
      yesterday: 'Ieri',
      thisWeek: 'Questa settimana'
    }
  },
  
  es: {
    nav: {
      dashboard: 'Panel',
      journal: 'Diario',
      mentalHealth: 'Salud mental',
      meetings: 'Reuniones',
      chat: 'Chatear con Tudor',
      library: 'Recursos',
      inbox: 'Bandeja de entrada',
      group: 'RR',
      menu: 'Menú',
      journey: 'Camino'
    },
    dashboard: {
      welcome: 'Bienvenido de nuevo',
      twoWeekWindow: 'Ventana de 2 semanas',
      week: 'Semana',
      dailyTasks: 'Tareas diarias',
      weeklyTasks: 'Tareas semanales',
      progress: 'Progreso',
      sobrietyClock: 'Reloj de sobriedad',
      notifications: 'Notificaciones'
    },
    tasks: {
      journal: 'Diario diario',
      mentalHealthCheck: 'Control de salud mental',
      personalHistory: 'Historia personal',
      weeklyReadings: 'Lecturas semanales',
      selfWork: 'Trabajo personal',
      meetingLog: 'Registro de reuniones',
      completed: 'Completado',
      pending: 'Pendiente',
      required: 'Obligatorio',
      optional: 'Opcional'
    },
    notifications: {
      urgent: 'URGENTE',
      drugScreenRequest: 'Prueba de drogas requerida',
      attendWithin24Hours: 'Por favor, acuda al laboratorio más cercano en 24 horas',
      uploadResults: 'Subir resultados',
      newMessage: 'Nuevo mensaje',
      taskReminder: 'Recordatorio de tarea',
      weekComplete: '¡Semana completada!',
      readyToProgress: 'Listo para pasar a la siguiente semana'
    },
    drugScreen: {
      title: 'Solicitud de prueba',
      instruction: 'Debe completar una prueba de drogas en orina',
      deadline: 'Fecha límite',
      labs: 'Laboratorios aprobados',
      uploadResults: 'Subir resultados',
      scanOrPhoto: 'Escanee o fotografíe sus resultados',
      yourId: 'Su número de identificación',
      status: 'Estado',
      pending: 'Pendiente',
      submitted: 'Enviado',
      verified: 'Verificado'
    },
    actions: {
      save: 'Guardar',
      submit: 'Enviar',
      cancel: 'Cancelar',
      continue: 'Continuar',
      back: 'Atrás',
      upload: 'Subir',
      download: 'Descargar',
      edit: 'Editar',
      delete: 'Eliminar',
      complete: 'Marcar como completado',
      viewAll: 'Ver todo',
      signIn: 'Iniciar sesión',
      signOut: 'Cerrar sesión',
      register: 'Registrarse'
    },
    messages: {
      loading: 'Cargando...',
      saving: 'Guardando...',
      saved: 'Guardado exitosamente',
      error: 'Ocurrió un error',
      required: 'Este campo es obligatorio',
      taskCompleted: '¡Tarea completada!',
      pleaseComplete: 'Por favor complete esta tarea',
      completeToProgress: 'Complete todas las tareas para progresar'
    },
    time: {
      days: 'días',
      hours: 'horas',
      minutes: 'minutos',
      seconds: 'segundos',
      today: 'Hoy',
      yesterday: 'Ayer',
      thisWeek: 'Esta semana'
    }
  },

  // Portuguese — menu labels only (9 Oct 2026).  All other keys fall back
  // to English via getTranslation() until the translation pipeline runs.
  pt: {
    nav: {
      dashboard: 'Painel',
      journal: 'Diário',
      mentalHealth: 'Saúde mental',
      meetings: 'Reuniões',
      chat: 'Converse com Tudor',
      library: 'Recursos',
      inbox: 'Caixa de entrada',
      group: 'RR',
      menu: 'Menu',
      journey: 'Jornada'
    },
    dashboard: {
      welcome: 'Bem-vindo de volta',
      twoWeekWindow: 'Janela de 2 Semanas',
      week: 'Semana',
      dailyTasks: 'Tarefas diárias',
      weeklyTasks: 'Tarefas semanais',
      progress: 'Progresso',
      sobrietyClock: 'Relógio de sobriedade',
      notifications: 'Notificações'
    }
  },

  // Mandarin (Simplified) — menu labels only (9 Oct 2026).
  zh: {
    nav: {
      dashboard: '仪表板',
      journal: '日记',
      mentalHealth: '心理健康',
      meetings: '会议',
      chat: '与 Tudor 聊天',
      library: '资源',
      inbox: '收件箱',
      group: 'RR',
      menu: '菜单',
      journey: '旅程'
    },
    dashboard: {
      welcome: '欢迎回来',
      twoWeekWindow: '2 周窗口',
      week: '周',
      dailyTasks: '每日任务',
      weeklyTasks: '每周任务',
      progress: '进度',
      sobrietyClock: '清醒计时器',
      notifications: '通知'
    }
  }
};

// ─── Quick check-in (Sep 2026 spec, Part 9) ──────────────────────────────
// All user-facing strings from Tudor's brief live here so translators can
// localise without touching component code.  English is filled; every
// other supported locale is marked TODO and falls back to the EN string
// via getTranslation() until a human translation is approved.
//
// DO NOT machine-translate these strings without Tudor's written approval.
const QUICK_CHECKIN_STRINGS_EN = {
  cardEyebrow: 'Quick check-in',
  cardBody1: "A short, private 2-minute check-in about how you've been over the past few days.",
  cardBody2: "Your answers stay on your device. We don't store them and we don't contact anyone. We only keep a count, with no names attached, of how many check-ins suggest someone may need extra support.",
  cardBody3: "This isn't a test. There's no right answer. If your answers suggest you need extra support, we'll show you who to call.",
  cardBeginBtn: 'Begin, about 2 minutes',
  cardSnoozeBtn: 'Remind me in an hour',
  greenHeading: 'Thanks for checking in',
  greenBody: "Your answers don't suggest you need extra support right now. Keep doing what's working: your meetings, your sponsor and your daily Recovery work.",
  greenFooter: 'If things change before your next check-in, the support list is always in the menu under "Get help now".',
  amberHeading: 'Some of your answers need attention',
  redHeading: 'Your answers suggest you may be at risk right now',
  disclaimerTitle: 'Quick check-in: privacy and limits',
  backToHome: 'Back to home',
  getHelpNow: 'Get help now',
};

// English is authoritative. Other locales fall back via getTranslation().
UI_TRANSLATIONS.en.quickCheckin = QUICK_CHECKIN_STRINGS_EN;
['ru', 'ko', 'uk', 'it', 'es', 'pt', 'zh'].forEach((code) => {
  if (UI_TRANSLATIONS[code]) {
    // Mark as pending — component code reads from EN via getTranslation
    // fallback until Tudor supplies approved translations.
    UI_TRANSLATIONS[code].quickCheckin = {
      __status: 'TODO: awaiting human translation',
    };
  }
});

// Helper function to get translation
export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let result = UI_TRANSLATIONS[lang] || UI_TRANSLATIONS['en'];
  
  for (const key of keys) {
    if (result && result[key]) {
      result = result[key];
    } else {
      // Fallback to English
      result = UI_TRANSLATIONS['en'];
      for (const k of keys) {
        if (result && result[k]) {
          result = result[k];
        } else {
          return path; // Return path if translation not found
        }
      }
      break;
    }
  }
  
  return result;
};

// Get language name by code
export const getLanguageName = (code) => {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang ? lang.nativeName : 'English';
};

// Detect browser language and return supported language code
export const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0];
  
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
  return supported ? supported.code : 'en';
};
